import { useEffect, useState } from "react";

import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Stack,
    CircularProgress,
    Divider,
} from "@mui/material";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { useAgendaAdmin } from "../../hooks/UseAgenda";



export const Agenda = () => {

    // =========================================
    // HOOK
    // =========================================

    const {
        agendas,
        loading,
        error,
        cargarAgendaAdmin,
        guardarAgenda,
        editarAgenda,
        borrarAgenda,
    } = useAgendaAdmin();


    // =========================================
    // MODAL
    // =========================================

    const [openModal, setOpenModal] =
        useState(false);


    const [agendaSeleccionada, setAgendaSeleccionada] =
        useState(null);


    // =========================================
    // FORMULARIO
    // =========================================

    const [formulario, setFormulario] =
        useState({
            titulo: "",
            fecha: "",
            hora: "",
            lugar: "",
            descripcion: "",
        });


    // =========================================
    // CARGAR
    // =========================================

    useEffect(() => {

        cargarAgendaAdmin();

    }, []);


    // =========================================
    // ABRIR CREAR
    // =========================================

    const abrirCrear = () => {

        setAgendaSeleccionada(null);

        setFormulario({
            titulo: "",
            fecha: "",
            hora: "",
            lugar: "",
            descripcion: "",
        });

        setOpenModal(true);

    };


    // =========================================
    // ABRIR EDITAR
    // =========================================

    const abrirEditar = (item) => {

        setAgendaSeleccionada(item);

        setFormulario({
            titulo: item.titulo || "",
            fecha: item.fecha
                ? item.fecha.substring(0, 10)
                : "",
            hora: item.hora
                ? item.hora.substring(0, 5)
                : "",
            lugar: item.lugar || "",
            descripcion:
                item.descripcion || "",
        });

        setOpenModal(true);

    };


    // =========================================
    // CERRAR
    // =========================================

    const cerrarModal = () => {

        setOpenModal(false);

        setAgendaSeleccionada(null);

    };


    // =========================================
    // INPUT
    // =========================================

    const handleChange = (event) => {

        const {
            name,
            value,
        } = event.target;

        setFormulario((prev) => ({
            ...prev,
            [name]: value,
        }));

    };


    // =========================================
    // GUARDAR
    // =========================================

    const handleSubmit = async () => {

        try {

            if (agendaSeleccionada) {

                await editarAgenda(
                    agendaSeleccionada.id,
                    formulario
                );

            } else {

                await guardarAgenda(
                    formulario
                );

            }

            cerrarModal();

        } catch (error) {

            console.error(error);

        }

    };


    // =========================================
    // ELIMINAR
    // =========================================

    const handleDelete = async (id) => {

        const confirmar =
            window.confirm(
                "¿Deseas eliminar esta actividad?"
            );

        if (!confirmar) {
            return;
        }

        await borrarAgenda(id);

    };


    return (

        <Container
            maxWidth="xl"
            sx={{
                py: 4,
            }}
        >

            {/* =========================================
                HEADER
            ========================================= */}

            <Box
                sx={{
                    display: "flex",

                    justifyContent:
                        "space-between",

                    alignItems: "center",

                    mb: 4,

                    gap: 2,

                    flexWrap: "wrap",
                }}
            >

                <Box>

                    <Typography
                        sx={{
                            fontSize: {
                                xs: "1.5rem",
                                md: "2rem",
                            },

                            fontWeight: 700,

                            color: "#561c33",
                        }}
                    >
                        Agenda
                    </Typography>

                    <Typography
                        sx={{
                            color: "#777",

                            mt: 0.5,
                        }}
                    >
                        Administra las actividades y eventos del colegio.
                    </Typography>

                </Box>


                <Button
                    variant="contained"

                    startIcon={
                        <AddRoundedIcon />
                    }

                    onClick={abrirCrear}

                    sx={{
                        bgcolor: "#561c33",

                        borderRadius: "12px",

                        px: 3,

                        py: 1.3,

                        textTransform: "none",

                        fontWeight: 700,

                        "&:hover": {
                            bgcolor: "#421426",
                        },
                    }}
                >
                    Nueva actividad
                </Button>

            </Box>


            {/* =========================================
                LOADING
            ========================================= */}

            {loading && !agendas.length ? (

                <Box
                    sx={{
                        minHeight: 300,

                        display: "flex",

                        alignItems: "center",

                        justifyContent: "center",
                    }}
                >

                    <CircularProgress />

                </Box>

            ) : (

                <Stack spacing={2}>

                    {agendas.map((item) => (

                        <Paper
                            key={item.id}

                            elevation={0}

                            sx={{
                                p: 3,

                                borderRadius: "20px",

                                border:
                                    "1px solid #eee",

                                transition: ".3s",

                                "&:hover": {
                                    boxShadow:
                                        "0 10px 30px rgba(0,0,0,.08)",
                                },
                            }}
                        >

                            <Box
                                sx={{
                                    display: "flex",

                                    justifyContent:
                                        "space-between",

                                    gap: 2,

                                    alignItems:
                                        "flex-start",
                                }}
                            >

                                <Box
                                    sx={{
                                        flex: 1,
                                    }}
                                >

                                    <Typography
                                        sx={{
                                            fontWeight: 700,

                                            fontSize:
                                                "1.15rem",

                                            color:
                                                "#2c2c2c",

                                            mb: 1,
                                        }}
                                    >
                                        {item.titulo}
                                    </Typography>


                                    <Stack
                                        direction={{
                                            xs: "column",
                                            sm: "row",
                                        }}

                                        spacing={{
                                            xs: 1,
                                            sm: 3,
                                        }}
                                    >

                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                        >

                                            <CalendarMonthRoundedIcon
                                                sx={{
                                                    fontSize: 19,
                                                    color: "#561c33",
                                                }}
                                            />

                                            <Typography
                                                color="text.secondary"
                                            >
                                                {item.fecha}
                                            </Typography>

                                        </Stack>


                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                        >

                                            <AccessTimeRoundedIcon
                                                sx={{
                                                    fontSize: 19,
                                                    color: "#561c33",
                                                }}
                                            />

                                            <Typography
                                                color="text.secondary"
                                            >
                                                {item.hora}
                                            </Typography>

                                        </Stack>


                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                        >

                                            <PlaceRoundedIcon
                                                sx={{
                                                    fontSize: 19,
                                                    color: "#561c33",
                                                }}
                                            />

                                            <Typography
                                                color="text.secondary"
                                            >
                                                {item.lugar}
                                            </Typography>

                                        </Stack>

                                    </Stack>

                                </Box>


                                {/* ACCIONES */}

                                <Stack
                                    direction="row"
                                    spacing={1}
                                >

                                    <IconButton
                                        onClick={() =>
                                            abrirEditar(
                                                item
                                            )
                                        }

                                        sx={{
                                            bgcolor:
                                                "#f5f5f5",

                                            color:
                                                "#561c33",
                                        }}
                                    >
                                        <EditRoundedIcon />
                                    </IconButton>


                                    <IconButton
                                        onClick={() =>
                                            handleDelete(
                                                item.id
                                            )
                                        }

                                        sx={{
                                            bgcolor:
                                                "#fff1f1",

                                            color:
                                                "#D32F2F",
                                        }}
                                    >
                                        <DeleteRoundedIcon />
                                    </IconButton>

                                </Stack>

                            </Box>


                            {item.descripcion && (

                                <>

                                    <Divider
                                        sx={{
                                            my: 2,
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            color: "#666",

                                            lineHeight:
                                                1.7,
                                        }}
                                    >
                                        {
                                            item.descripcion
                                        }
                                    </Typography>

                                </>

                            )}

                        </Paper>

                    ))}

                </Stack>

            )}


            {/* =========================================
                MODAL
            ========================================= */}

            <Dialog
                open={openModal}

                onClose={cerrarModal}

                fullWidth

                maxWidth="sm"

                PaperProps={{
                    sx: {
                        borderRadius:
                            "24px",
                    },
                }}
            >

                {/* HEADER */}

                <DialogTitle
                    sx={{
                        px: 4,
                        pt: 3,
                        pb: 2,
                    }}
                >

                    <Box
                        sx={{
                            display: "flex",

                            justifyContent:
                                "space-between",

                            alignItems:
                                "center",
                        }}
                    >

                        <Box>

                            <Typography
                                sx={{
                                    fontWeight: 700,

                                    fontSize:
                                        "1.4rem",

                                    color:
                                        "#561c33",
                                }}
                            >
                                {agendaSeleccionada
                                    ? "Editar actividad"
                                    : "Nueva actividad"}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize:
                                        ".85rem",

                                    color:
                                        "#888",

                                    mt: .5,
                                }}
                            >
                                Completa la información de la actividad.
                            </Typography>

                        </Box>


                        <IconButton
                            onClick={
                                cerrarModal
                            }
                        >

                            <CloseRoundedIcon />

                        </IconButton>

                    </Box>

                </DialogTitle>


                {/* CONTENIDO */}

                <DialogContent
                    sx={{
                        px: 4,
                    }}
                >

                    <Stack spacing={2.5}>

                        <TextField
                            label="Título"
                            name="titulo"
                            value={
                                formulario.titulo
                            }
                            onChange={
                                handleChange
                            }

                            fullWidth

                            required
                        />


                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}

                            spacing={2}
                        >

                            <TextField
                                label="Fecha"
                                name="fecha"

                                type="date"

                                value={
                                    formulario.fecha
                                }

                                onChange={
                                    handleChange
                                }

                                fullWidth

                                required

                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />


                            <TextField
                                label="Hora"
                                name="hora"

                                type="time"

                                value={
                                    formulario.hora
                                }

                                onChange={
                                    handleChange
                                }

                                fullWidth

                                required

                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />

                        </Stack>


                        <TextField
                            label="Lugar"
                            name="lugar"

                            value={
                                formulario.lugar
                            }

                            onChange={
                                handleChange
                            }

                            fullWidth

                            required
                        />


                        <TextField
                            label="Descripción"
                            name="descripcion"

                            value={
                                formulario.descripcion
                            }

                            onChange={
                                handleChange
                            }

                            fullWidth

                            multiline

                            minRows={4}
                        />

                    </Stack>

                </DialogContent>


                {/* ACCIONES */}

                <DialogActions
                    sx={{
                        px: 4,

                        pb: 3,

                        pt: 2,
                    }}
                >

                    <Button
                        onClick={
                            cerrarModal
                        }

                        sx={{
                            color: "#777",

                            textTransform:
                                "none",

                            fontWeight: 600,
                        }}
                    >
                        Cancelar
                    </Button>


                    <Button
                        variant="contained"

                        onClick={
                            handleSubmit
                        }

                        disabled={loading}

                        sx={{
                            bgcolor:
                                "#561c33",

                            borderRadius:
                                "10px",

                            px: 3,

                            textTransform:
                                "none",

                            fontWeight: 700,

                            "&:hover": {
                                bgcolor:
                                    "#421426",
                            },
                        }}
                    >
                        {loading
                            ? "Guardando..."
                            : agendaSeleccionada
                                ? "Guardar cambios"
                                : "Crear actividad"}
                    </Button>

                </DialogActions>

            </Dialog>

        </Container>

    );

};